import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { BetSlipType, Odds } from '@bpos';
import { SlipState } from '@bpos/v1/my-bets';
import { MyBetsConfig, Sitecore, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { BetslipListType, MyBetsImage, MyBetsImages } from '@frontend/sports/types/models/my-bets';

import { EarlyPayoutUtils } from '../../../my-bets-base/early-payout/early-payout-utils';
import { MyBetsBet, MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsOddsPipe } from '../../../my-bets-base/pipes/my-bets-odds.pipe';
import { BetslipUtilService } from '../../services/betslip-util.service';

@Component({
    selector: 'ms-my-bets-bet-card-title',
    templateUrl: 'my-bets-bet-card-title.component.html',
    styleUrls: ['my-bets-bet-card-title.component.scss', '../../styles/original-odds.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsBetCardTitleComponent {
    bet = input<MyBetsBet>();
    betslip = input.required<MyBetsBetSlip>();
    myBetsListContext = input.required<MyBetsListContext>();
    showEditButton = input<boolean>();
    showBetOdds = input<boolean>();
    showOutcomeBadge = input<boolean>();
    shouldApplyPaddings = input<boolean>();

    private sitecoreMyBets = inject(SitecoreMyBetsConfig);
    private earlyPayoutUtils = inject(EarlyPayoutUtils);
    siteCore = inject(Sitecore);
    mybetsConfig = inject(MyBetsConfig);
    betslipUtilService = inject(BetslipUtilService);
    myBetsOddsPipe = inject(MyBetsOddsPipe);

    backgroundImage = signal(this.sitecoreMyBets.myBetsImages.find((image: MyBetsImage) => image.name === MyBetsImages.WonBackground));
    brandLogoImage = signal(this.sitecoreMyBets.myBetsImages.find((image: MyBetsImage) => image.name === MyBetsImages.BrandLogo));

    get showEditBet(): boolean {
        return !!this.showEditButton() && this.earlyPayoutUtils.isEditButtonVisible(this.betslip(), this.myBetsListContext());
    }

    isOutcomeBadgeVisible = computed(() => {
        if (!this.showOutcomeBadge()) {
            return false;
        }
        if (this.betslip().isRecoveringAfterEditBet || this.betslip().isCachedOut) {
            return true;
        }

        if (this.betslip().state === SlipState.Open || this.myBetsListContext().isEventView) {
            return false;
        }

        return this.myBetsListContext().listType === BetslipListType.Settled;
    });

    totalOddsValue = computed(() => {
        const isStartingPrice = this.betslip().bets.some((bet) => bet.participantInfo?.isStartingPrice || bet.optionMarketInfo?.isStartingPrice);
        if (isStartingPrice) {
            return 'SP';
        }

        if (!this.betslip().totalOdds) {
            return '';
        }

        return this.myBetsOddsPipe.transform(this.totalOdds());
    });

    isBetBuilder = computed(() => {
        return !this.betslip().isSingleBet || this.betslip().bets[0].betBuilderInfo !== null;
    });

    private totalOdds = computed<Odds | undefined>(() => {
        if (this.betslip().betType === BetSlipType.Single) {
            return this.betslip().bets[0]?.odds;
        }

        return this.betslip().totalOdds;
    });

    isWon = computed(() => {
        return this.betslip().state === SlipState.Won && !this.betslip().isCachedOut;
    });

    isWonWithBrandLogo = computed(() => {
        return this.isWon() && !!this.brandLogoImage();
    });

    get oddsAreBoosted(): boolean {
        return this.betslipUtilService.oddsAreBoosted(this.betslip());
    }

    get isSGPRepricedOdds(): boolean {
        return this.mybetsConfig.enableSGPRepricedOdds && (this.betslip().isOGPRepricePending || !!this.betslip().totalOGPRepricedOdds);
    }

    boostedOdds = computed(() => {
        return this.betslip().usedRewardToken?.additionalInformation?.boostedNativeOdds;
    });
}
